import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from './Modal';
import Button from './ui/Button';
import { ConfirmModal } from './ui/ConfirmModal';
import EmptyState from './ui/EmptyState';
import type { SelectId } from './MultiSelect';
import { compareNames, matchesQuery } from '../utils/text';
import { fetchGroupMemberIds } from '../utils/groupMembers';
import './AssignmentDrawer.css';

export interface AssignmentGroupRef {
  _id: SelectId;
  name: string;
  /** Live group size from `GET /api/groups`, when the caller has it. */
  memberCount?: number;
}

export interface AssignmentPerson {
  _id: SelectId;
  name: string;
  /** The person's own group memberships — used to group the roster under labels. */
  groups?: AssignmentGroupRef[];
  /** false = outside the viewer's scope (D18): name only, removable, not addable. */
  canViewDetails?: boolean;
}

export interface AssignmentValue {
  memberIds: SelectId[];
  groupIds: SelectId[];
}

interface AssignmentDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  /** Dialog title, e.g. `Assign — Monday training`. */
  title: string;
  people: AssignmentPerson[];
  /**
   * Assignable groups. **Omit for a people-only drawer** — the Groups tab, the
   * switcher and the provenance column all disappear.
   */
  groups?: AssignmentGroupRef[];
  value: AssignmentValue;
  onApply: (next: AssignmentValue) => void;
  /**
   * Labels already saved on the record. Any other label is applied by the
   * server on save (its current members are added), so those members are locked.
   */
  savedGroupIds?: SelectId[];
  /** Loads a group's current members when it is picked. */
  loadGroupMembers?: (groupId: SelectId) => Promise<SelectId[]>;
}

type Mode = 'people' | 'groups';
type Seg = 'added' | 'missing' | 'all';

interface RemovePrompt {
  names: string[];
  memberIds: string[];
}

const key = (id: SelectId) => String(id);

/** Roster entries from an event/template GET (`members[]` objects), as people. */
export const rosterPeopleFrom = (members: unknown): AssignmentPerson[] =>
  Array.isArray(members)
    ? members
        .filter((m): m is { _id: SelectId; name: string; canViewDetails?: boolean } =>
          !!m && typeof m === 'object' && 'name' in m && '_id' in m)
        .map((m) => ({ _id: m._id, name: m.name, canViewDetails: m.canViewDetails }))
    : [];

/** Appends roster entries missing from the scoped people list as name-only rows. */
export const mergeRosterPeople = (
  people: AssignmentPerson[],
  roster?: AssignmentPerson[],
): AssignmentPerson[] => {
  if (!roster || roster.length === 0) return people;
  const known = new Set(people.map((p) => key(p._id)));
  const extra: AssignmentPerson[] = [];
  roster.forEach((r) => {
    if (known.has(key(r._id))) return;
    known.add(key(r._id));
    extra.push({ _id: r._id, name: r.name, canViewDetails: false });
  });
  return extra.length > 0 ? [...people, ...extra] : people;
};

/**
 * One surface for deciding who is assigned to an event, template or group.
 *
 * Groups are a copy, not a live source (roster plan D2/D15): picking a group
 * adds its current members to the roster and keeps the group as a display
 * label; dropping a label removes nobody unless the admin asks to.
 *
 * Assignment is not attendance — this drawer never touches `event_attendance`.
 */
const AssignmentDrawer: React.FC<AssignmentDrawerProps> = ({
  isOpen,
  onClose,
  title,
  people,
  groups,
  value,
  onApply,
  savedGroupIds,
  loadGroupMembers = fetchGroupMemberIds,
}) => {
  const { t } = useTranslation();
  /** No groups passed → people-only drawer: no switcher, no provenance column. */
  const groupsEnabled = Array.isArray(groups) && groups.length > 0;
  const [mode, setMode] = useState<Mode>('people');
  const [seg, setSeg] = useState<Seg>('added');
  const [query, setQuery] = useState('');
  const [pendingMembers, setPendingMembers] = useState<Set<string>>(new Set());
  const [pendingGroups, setPendingGroups] = useState<Set<string>>(new Set());
  const [fetchedMembers, setFetchedMembers] = useState<Map<string, Set<string>>>(new Map());
  // Members each label added in this session, so unpicking it can revert them.
  const [sessionAdds, setSessionAdds] = useState<Map<string, Set<string>>>(new Map());
  const [loadingGroups, setLoadingGroups] = useState<Set<string>>(new Set());
  const [removePrompt, setRemovePrompt] = useState<RemovePrompt | null>(null);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  // Lets a late group fetch see whether its label is still picked.
  const pendingGroupsRef = useRef(pendingGroups);
  pendingGroupsRef.current = pendingGroups;
  const pendingMembersRef = useRef(pendingMembers);
  pendingMembersRef.current = pendingMembers;

  // Re-seed from the committed value each time the drawer opens, so a cancelled
  // session never leaks into the next one.
  useEffect(() => {
    if (!isOpen) return;
    setPendingMembers(new Set(value.memberIds.map(key)));
    setPendingGroups(new Set(value.groupIds.map(key)));
    setLoadingGroups(new Set());
    setFetchedMembers(new Map());
    setSessionAdds(new Map());
    setRemovePrompt(null);
    setMode('people');
    setSeg('added');
    setQuery('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Guard against a parent dropping `groups` while the Groups tab is open.
  useEffect(() => {
    if (!groupsEnabled && mode === 'groups') setMode('people');
  }, [groupsEnabled, mode]);

  const committed = useMemo(() => new Set(value.memberIds.map(key)), [value.memberIds]);
  /** Out-of-scope people can only stay on, or leave, a roster they were already on. */
  const outOfScope = (p: AssignmentPerson) => p.canViewDetails === false;
  const lockedOut = (p: AssignmentPerson) => outOfScope(p) && !committed.has(key(p._id));
  const sortedPeople = useMemo(
    () =>
      people
        .filter((p) => !(p.canViewDetails === false && !committed.has(key(p._id))))
        .sort((a, b) => compareNames(a.name, b.name)),
    [people, committed],
  );
  const sortedGroups = useMemo(
    () => (groups ?? []).slice().sort((a, b) => compareNames(a.name, b.name)),
    [groups],
  );

  // Group → member ids from the people list; a fetched list wins when present.
  const derivedMembers = useMemo(() => {
    const map = new Map<string, Set<string>>();
    people.forEach((p) => {
      (p.groups ?? []).forEach((g) => {
        const gk = key(g._id);
        if (!map.has(gk)) map.set(gk, new Set());
        map.get(gk)!.add(key(p._id));
      });
    });
    return map;
  }, [people]);

  const membersOf = (gk: string): Set<string> =>
    fetchedMembers.get(gk) ?? derivedMembers.get(gk) ?? new Set();

  const savedSet = useMemo(
    () => new Set((savedGroupIds ?? value.groupIds).map(key)),
    [savedGroupIds, value.groupIds],
  );

  /** Pending labels in display order. */
  const labels = sortedGroups.filter((g) => pendingGroups.has(key(g._id)));

  const isAssigned = (p: AssignmentPerson) => pendingMembers.has(key(p._id));
  const labelOf = (p: AssignmentPerson): AssignmentGroupRef | null =>
    labels.find((g) => membersOf(key(g._id)).has(key(p._id))) ?? null;
  /** A label not yet saved re-adds its members on save, so they can't be unticked now. */
  const lockedBy = (p: AssignmentPerson): AssignmentGroupRef | null =>
    labels.find((g) => !savedSet.has(key(g._id)) && membersOf(key(g._id)).has(key(p._id))) ?? null;

  // Full roster, including ids the scoped people list cannot name.
  const assignedCount = pendingMembers.size;
  const listedOnCount = sortedPeople.filter(isAssigned).length;
  const groupSize = (g: AssignmentGroupRef) => {
    const gk = key(g._id);
    return fetchedMembers.get(gk)?.size ?? g.memberCount ?? membersOf(gk).size;
  };
  const presentFromGroup = (g: AssignmentGroupRef) =>
    [...membersOf(key(g._id))].filter((id) => pendingMembers.has(id)).length;
  /** People this group would newly add — not merely its size. */
  const newFromGroup = (g: AssignmentGroupRef) =>
    [...membersOf(key(g._id))].filter((id) => !pendingMembers.has(id)).length;

  const visiblePeople = sortedPeople.filter((p) => {
    const on = isAssigned(p);
    if (seg === 'added' && !on) return false;
    if (seg === 'missing' && on) return false;
    return query.trim() === '' || matchesQuery(p.name, query);
  });

  const visibleGroups = sortedGroups.filter((g) => {
    const on = pendingGroups.has(key(g._id));
    if (seg === 'added' && !on) return false;
    if (seg === 'missing' && on) return false;
    return query.trim() === '' || matchesQuery(g.name, query);
  });

  // Diff is reported in people, because that is the consequence an admin cares about.
  const diff = useMemo(() => {
    const before = new Set(value.memberIds.map(key));
    const beforeGroups = new Set(value.groupIds.map(key));
    let added = 0;
    let removed = 0;
    pendingMembers.forEach((id) => {
      if (!before.has(id)) added += 1;
    });
    before.forEach((id) => {
      if (!pendingMembers.has(id)) removed += 1;
    });
    let groupsAdded = 0;
    let groupsRemoved = 0;
    pendingGroups.forEach((id) => {
      if (!beforeGroups.has(id)) groupsAdded += 1;
    });
    beforeGroups.forEach((id) => {
      if (!pendingGroups.has(id)) groupsRemoved += 1;
    });
    return { added, removed, groupsAdded, groupsRemoved };
  }, [pendingMembers, pendingGroups, value]);

  const isDirty =
    diff.added > 0 || diff.removed > 0 || diff.groupsAdded > 0 || diff.groupsRemoved > 0;

  const togglePerson = (p: AssignmentPerson, on: boolean) => {
    setPendingMembers((prev) => {
      const next = new Set(prev);
      if (on) next.add(key(p._id));
      else next.delete(key(p._id));
      return next;
    });
  };

  /** Picking a group: keep it as a label and add its current members. */
  const applyGroups = (list: AssignmentGroupRef[]) => {
    const fresh = list.filter((g) => !pendingGroups.has(key(g._id)));
    if (fresh.length === 0) return;
    setPendingGroups((prev) => {
      const next = new Set(prev);
      fresh.forEach((g) => next.add(key(g._id)));
      return next;
    });
    setLoadingGroups((prev) => {
      const next = new Set(prev);
      fresh.forEach((g) => next.add(key(g._id)));
      return next;
    });
    fresh.forEach((g) => {
      const gk = key(g._id);
      loadGroupMembers(g._id)
        .then((ids) => new Set(ids.map(key)))
        // Fall back to the people list's own group links.
        .catch(() => derivedMembers.get(gk) ?? new Set<string>())
        .then((ids) => {
          setFetchedMembers((prev) => new Map(prev).set(gk, ids));
          if (pendingGroupsRef.current.has(gk)) {
            const added = [...ids].filter((id) => !pendingMembersRef.current.has(id));
            setSessionAdds((prev) => new Map(prev).set(gk, new Set(added)));
            setPendingMembers((prev) => new Set([...prev, ...ids]));
          }
          setLoadingGroups((prev) => {
            const next = new Set(prev);
            next.delete(gk);
            return next;
          });
        });
    });
  };

  /** Dropping labels removes nobody; offer to remove their members as well. */
  const removeGroups = (list: AssignmentGroupRef[]) => {
    const dropping = list.filter((g) => pendingGroups.has(key(g._id)));
    if (dropping.length === 0) return;
    const dropKeys = new Set(dropping.map((g) => key(g._id)));
    setPendingGroups((prev) => {
      const next = new Set(prev);
      dropKeys.forEach((gk) => next.delete(gk));
      return next;
    });
    // Members still covered by another remaining label stay out of the offer.
    const kept = new Set<string>();
    labels
      .filter((g) => !dropKeys.has(key(g._id)))
      .forEach((g) => membersOf(key(g._id)).forEach((id) => kept.add(id)));

    // A label picked in this session just reverts its own additions, no prompt.
    const savedLabels = new Set(value.groupIds.map(key));
    const sessionPicked = dropping.filter((g) => !savedLabels.has(key(g._id)));
    const reverted = new Set<string>();
    sessionPicked.forEach((g) =>
      sessionAdds.get(key(g._id))?.forEach((id) => {
        if (!kept.has(id)) reverted.add(id);
      }),
    );
    if (sessionPicked.length > 0) {
      setSessionAdds((prev) => {
        const next = new Map(prev);
        sessionPicked.forEach((g) => next.delete(key(g._id)));
        return next;
      });
      if (reverted.size > 0) {
        setPendingMembers((prev) => new Set([...prev].filter((id) => !reverted.has(id))));
      }
    }

    const savedDropping = dropping.filter((g) => savedLabels.has(key(g._id)));
    const candidates = new Set<string>();
    savedDropping.forEach((g) =>
      membersOf(key(g._id)).forEach((id) => {
        if (pendingMembers.has(id) && !kept.has(id) && !reverted.has(id)) candidates.add(id);
      }),
    );
    if (candidates.size > 0) {
      setRemovePrompt({ names: savedDropping.map((g) => g.name), memberIds: [...candidates] });
    }
  };

  const bulk = (on: boolean) => {
    if (mode === 'people') {
      setPendingMembers((prev) => {
        const next = new Set(prev);
        visiblePeople.forEach((p) => {
          if (!on && lockedBy(p)) return;
          if (on && lockedOut(p)) return;
          if (on) next.add(key(p._id));
          else next.delete(key(p._id));
        });
        return next;
      });
    } else if (on) {
      applyGroups(visibleGroups);
    } else {
      removeGroups(visibleGroups);
    }
  };

  const requestClose = () => {
    if (isDirty) setConfirmDiscard(true);
    else onClose();
  };

  const handleApply = () => {
    onApply({
      memberIds: Array.from(pendingMembers),
      groupIds: Array.from(pendingGroups),
    });
    onClose();
  };

  const bulkEnabled =
    mode === 'people' ? visiblePeople.length > 0 : visibleGroups.length > 0;

  const segCounts =
    mode === 'people'
      ? { added: listedOnCount, missing: sortedPeople.length - listedOnCount, all: sortedPeople.length }
      : {
          added: pendingGroups.size,
          missing: sortedGroups.length - pendingGroups.size,
          all: sortedGroups.length,
        };

  const renderPerson = (p: AssignmentPerson) => {
    const on = isAssigned(p);
    const label = on ? labelOf(p) : null;
    const lock = on ? lockedBy(p) : null;
    const blocked = lock !== null || (!on && lockedOut(p));
    const source = label
      ? t('assign.viaGroup', { group: label.name })
      : on
        ? t('assign.direct')
        : '—';
    return (
      <tr
        key={key(p._id)}
        className={label ? 'asg__row asg__row--via' : on ? 'asg__row asg__row--on' : 'asg__row'}
        data-testid="asg-person"
        data-id={key(p._id)}
        data-name={p.name}
        data-source={label ? 'group' : on ? 'direct' : 'none'}
        data-locked={String(blocked)}
        data-on={String(on)}
        data-out-of-scope={String(outOfScope(p))}
      >
        <td className="asg__cb-col">
          <input
            type="checkbox"
            checked={on}
            disabled={blocked}
            aria-disabled={blocked}
            aria-label={p.name}
            title={lock
              ? t('assign.pendingGroupHint', { group: lock.name })
              : outOfScope(p) ? t('assign.outOfScopeHint') : undefined}
            onChange={(e) => togglePerson(p, e.target.checked)}
          />
        </td>
        <td>
          <span className="asg__name">{p.name}</span>
          {outOfScope(p) && <span className="asg__scope">{t('assign.outOfScopeHint')}</span>}
          {groupsEnabled && <span className="asg__sub">{source}</span>}
        </td>
        {groupsEnabled && (
          <td className="asg__from-col">
            <span className={label ? 'asg__from asg__from--via' : 'asg__from'}>{source}</span>
          </td>
        )}
      </tr>
    );
  };

  // With labels applied, the roster reads grouped: each label, then Direct, then the rest.
  const peopleSections = (() => {
    if (!groupsEnabled || labels.length === 0) return null;
    const placed = new Set<string>();
    const sections: Array<{ id: string; title: string; rows: AssignmentPerson[] }> = [];
    labels.forEach((g) => {
      const rows = visiblePeople.filter(
        (p) => isAssigned(p) && !placed.has(key(p._id)) && labelOf(p)?._id === g._id,
      );
      rows.forEach((p) => placed.add(key(p._id)));
      sections.push({
        id: `g-${key(g._id)}`,
        title: t('assign.sectionGroup', {
          group: g.name,
          present: presentFromGroup(g),
          size: groupSize(g),
        }),
        rows,
      });
    });
    sections.push({
      id: 'direct',
      title: t('assign.sectionDirect'),
      rows: visiblePeople.filter((p) => isAssigned(p) && !placed.has(key(p._id))),
    });
    sections.push({
      id: 'none',
      title: t('assign.sectionNotAdded'),
      rows: visiblePeople.filter((p) => !isAssigned(p)),
    });
    return sections.filter((s) => s.rows.length > 0);
  })();

  return (
    <>
      <Modal isOpen={isOpen} onClose={requestClose} title={title} size="large">
        <div className="asg" data-testid="assignment-drawer" data-mode-active={mode} data-groups-enabled={String(groupsEnabled)}>
          {groupsEnabled && (
          <div className="asg__mode" role="tablist" aria-label={t('assign.whatToManage')}>
            {(['people', 'groups'] as Mode[]).map((m) => (
              <button
                key={m}
                type="button"
                role="tab"
                aria-selected={mode === m}
                className={`asg__mode-btn asg__mode-btn--${m}`}
                data-testid="asg-mode"
                data-mode={m}
                onClick={() => {
                  setMode(m);
                  setQuery('');
                }}
              >
                {m === 'people' ? t('assign.tabPeople') : t('assign.tabGroups')}
                <span className="asg__mode-count">
                  {m === 'people' ? assignedCount : pendingGroups.size}
                </span>
              </button>
            ))}
          </div>
          )}

          <div className="asg__filters">
            <input
              type="search"
              className="asg__search"
              data-testid="asg-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={
                mode === 'people' ? t('assign.searchPeople') : t('assign.searchGroups')
              }
              aria-label={mode === 'people' ? t('assign.searchPeople') : t('assign.searchGroups')}
            />
          </div>

          <div className="asg__segs" role="tablist" aria-label={t('assign.stateFilter')}>
            {(['added', 'missing', 'all'] as Seg[]).map((s) => (
              <button
                key={s}
                type="button"
                role="tab"
                aria-selected={seg === s}
                className="asg__seg-btn"
                data-testid="asg-seg"
                data-seg={s}
                onClick={() => setSeg(s)}
              >
                {t(`assign.seg_${s}`)} <span className="asg__seg-count">{segCounts[s]}</span>
              </button>
            ))}
          </div>

          {groupsEnabled && (
            <p className="asg__hint" data-testid="asg-hint">
              {mode === 'groups' ? t('assign.groupsAreCopied') : t('assign.notAttendance')}
            </p>
          )}

          <div className="asg__body">
            {mode === 'people' ? (
              visiblePeople.length === 0 ? (
                <EmptyState title={t('assign.noPeopleMatch')} />
              ) : (
                <table className="asg__table">
                  <thead>
                    <tr>
                      <th scope="col" className="asg__cb-col" />
                      <th scope="col">{t('assign.colName')}</th>
                      {groupsEnabled && (
                        <th scope="col" className="asg__from-col">{t('assign.colFrom')}</th>
                      )}
                    </tr>
                  </thead>
                  {peopleSections ? (
                    peopleSections.map((s) => (
                      <tbody key={s.id} data-testid="asg-section" data-section={s.id}>
                        <tr className="asg__section">
                          <th scope="colgroup" colSpan={3}>{s.title}</th>
                        </tr>
                        {s.rows.map(renderPerson)}
                      </tbody>
                    ))
                  ) : (
                    <tbody>{visiblePeople.map(renderPerson)}</tbody>
                  )}
                </table>
              )
            ) : visibleGroups.length === 0 ? (
              <EmptyState title={t('assign.noGroupsMatch')} />
            ) : (
              <table className="asg__table">
                <thead>
                  <tr>
                    <th scope="col" className="asg__cb-col" />
                    <th scope="col">{t('assign.colGroup')}</th>
                    <th scope="col" className="asg__from-col">{t('assign.colMembers')}</th>
                    <th scope="col" className="asg__from-col">{t('assign.colImpact')}</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleGroups.map((g) => {
                    const gk = key(g._id);
                    const on = pendingGroups.has(gk);
                    const loadingRow = loadingGroups.has(gk);
                    const fresh = newFromGroup(g);
                    return (
                      <tr
                        key={gk}
                        className={on ? 'asg__row asg__row--via' : 'asg__row'}
                        data-testid="asg-group"
                        data-id={gk}
                        data-name={g.name}
                        data-on={String(on)}
                        data-loading={String(loadingRow)}
                      >
                        <td className="asg__cb-col">
                          <input
                            type="checkbox"
                            checked={on}
                            disabled={loadingRow}
                            aria-label={g.name}
                            onChange={(e) => (e.target.checked ? applyGroups([g]) : removeGroups([g]))}
                          />
                        </td>
                        <td>
                          <span className="asg__name">{g.name}</span>
                        </td>
                        <td className="asg__from-col">
                          <span className="asg__from">{groupSize(g)}</span>
                        </td>
                        <td className="asg__from-col">
                          {loadingRow ? (
                            <span className="asg__from">{t('common.loading')}</span>
                          ) : on ? (
                            <span className="asg__from asg__from--via">
                              {t('assign.groupOnRoster', { present: presentFromGroup(g), size: groupSize(g) })}
                            </span>
                          ) : fresh > 0 ? (
                            <span className="asg__from asg__from--new">
                              {t('assign.groupWouldAdd', { count: fresh })}
                            </span>
                          ) : (
                            <span className="asg__from">{t('assign.groupAllPresent')}</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          <div className="asg__bulk">
            <Button variant="secondary" size="sm" data-testid="asg-bulk-add" onClick={() => bulk(true)} disabled={!bulkEnabled}>
              {mode === 'people' ? t('assign.addAllShown') : t('assign.addShownGroups')}
            </Button>
            <Button variant="secondary" size="sm" data-testid="asg-bulk-remove" onClick={() => bulk(false)} disabled={!bulkEnabled}>
              {mode === 'people' ? t('assign.removeAllShown') : t('assign.removeShownGroups')}
            </Button>
            <span className="asg__shown" data-testid="asg-shown">
              {t('assign.shownOf', {
                shown: mode === 'people' ? visiblePeople.length : visibleGroups.length,
                total: mode === 'people' ? sortedPeople.length : sortedGroups.length,
              })}
            </span>
          </div>

          <div className="asg__foot">
            <span className="asg__diff" data-testid="asg-diff" data-added={diff.added} data-removed={diff.removed}>
              {!isDirty ? (
                <span className="asg__diff-none">{t('assign.noChanges')}</span>
              ) : (
                <>
                  {(diff.groupsAdded > 0 || diff.groupsRemoved > 0) && (
                    <span className="asg__diff-groups">
                      {t('assign.diffGroups', {
                        added: diff.groupsAdded,
                        removed: diff.groupsRemoved,
                      })}
                    </span>
                  )}
                  <span className="asg__diff-plus">+{diff.added}</span>
                  <span className="asg__diff-minus">−{diff.removed}</span>
                </>
              )}
            </span>
            <span className="asg__actions">
              <Button variant="secondary" data-testid="asg-cancel" onClick={requestClose}>
                {t('common.cancel')}
              </Button>
              <Button
                variant="primary"
                data-testid="asg-apply"
                onClick={handleApply}
                disabled={loadingGroups.size > 0}
              >
                {t('assign.applyTo', { count: assignedCount })}
              </Button>
            </span>
          </div>
        </div>
      </Modal>

      <ConfirmModal
        isOpen={removePrompt !== null}
        title={t('assign.removeLabelTitle')}
        message={t('assign.removeLabelMessage', {
          groups: removePrompt?.names.join(', ') ?? '',
          count: removePrompt?.memberIds.length ?? 0,
        })}
        confirmText={t('assign.removeLabelMembers', { count: removePrompt?.memberIds.length ?? 0 })}
        cancelText={t('assign.keepMembers')}
        variant="danger"
        onConfirm={() => {
          const drop = new Set(removePrompt?.memberIds ?? []);
          setPendingMembers((prev) => new Set([...prev].filter((id) => !drop.has(id))));
          setRemovePrompt(null);
        }}
        onClose={() => setRemovePrompt(null)}
      />

      <ConfirmModal
        isOpen={confirmDiscard}
        title={t('assign.discardTitle')}
        message={t('assign.discardMessage')}
        confirmText={t('assign.discardConfirm')}
        onConfirm={() => {
          setConfirmDiscard(false);
          onClose();
        }}
        onClose={() => setConfirmDiscard(false)}
      />
    </>
  );
};

export default AssignmentDrawer;
